<template>
    <div class="vc-checkout-payment-stripe l-form l-form--stacked">
        <div class="vc-checkout-payment-stripe__fields">
            <base-control :constraints="constraints.cardholderName"
                          :id="formFieldsIds.stripeCardholderName"
                          :triggerValidation="stripeValidationTriggered"
                          @validated="validation.cardholderName = $event"
                          key="card-holder-name"
                          label="Cardholder Name"
                          name="cardholder"
                          type="text"
                          v-model="cardholderName"></base-control>
            <base-stripe-control :id="formFieldsIds.stripeCardNumber"
                                 :stripe="stripePk"
                                 :triggerValidation="stripeValidationTriggered"
                                 @validated="validation.cardNumber = $event"
                                 key="card-number"
                                 label="Card Number"
                                 type="cardNumber"></base-stripe-control>
            <base-stripe-control :id="formFieldsIds.stripeCardExpiry"
                                 :stripe="stripePk"
                                 :triggerValidation="stripeValidationTriggered"
                                 @validated="validation.cardExpiry = $event"
                                 key="card-expiry"
                                 label="Card Expiry: MM/YY"
                                 type="cardExpiry"></base-stripe-control>
            <base-stripe-control :id="formFieldsIds.stripeCardCvc"
                                 :stripe="stripePk"
                                 :triggerValidation="stripeValidationTriggered"
                                 @validated="validation.cardCvc = $event"
                                 key="card-cvc"
                                 label="Card CVC"
                                 type="cardCvc"></base-stripe-control>
            <template v-if="stripeRequestAddress">
                <base-control :constraints="constraints.addressLine1"
                              :id="formFieldsIds.stripeAddressLine1"
                              :triggerValidation="stripeValidationTriggered"
                              @validated="validation.addressLine1 = $event"
                              key="addressLine1"
                              label="Street Address"
                              name="address[line_1]"
                              type="text"
                              v-model="addressLine1"></base-control>
                <base-control :id="formFieldsIds.stripeAddressLine2"
                              key="addressLine2"
                              label="Address Line 2"
                              name="address[line_2]"
                              type="text"
                              v-model="addressLine2"></base-control>
                <base-control :constraints="constraints.city"
                              :id="formFieldsIds.stripeCity"
                              :triggerValidation="stripeValidationTriggered"
                              @validated="validation.city = $event"
                              key="city"
                              label="City"
                              name="address[city]"
                              type="text"
                              v-model="city"></base-control>
                <base-control :constraints="constraints.country"
                              :id="formFieldsIds.stripeCountry"
                              :selectOptions="countries"
                              :triggerValidation="stripeValidationTriggered"
                              @toggle="onSelectCountryToggle"
                              @validated="validation.country = $event"
                              key="country"
                              label="Country"
                              name="address[country]"
                              type="select"
                              v-model="country"></base-control>
            </template>
            <base-stripe-control :id="formFieldsIds.stripePostZipCode"
                                 :stripe="stripePk"
                                 :triggerValidation="stripeValidationTriggered"
                                 @input="postZipCode = $event"
                                 @validated="validation.postZipCode = $event"
                                 key="post-zip-code"
                                 label="Post / ZIP Code"
                                 name="address[code]"
                                 type="postalCode"></base-stripe-control>

        </div>
        <base-transition-expand>
            <base-alert class="vc-checkout-payment-stripe__error" type="error" v-if="stripePaymentError">
                {{stripePaymentError}}
            </base-alert>
        </base-transition-expand>

        <button :disabled="stripeProcessing" @click="tryPayment"
                class="vc-checkout-payment-stripe__submit c-button c-button--default c-button--large-height"
                type="button">
            <template v-if="!stripeProcessing">
                Place your order
            </template>
            <template v-else>
                <font-awesome-icon class="c-button__left-icon" icon="spinner-third"
                                   spin></font-awesome-icon>
                Processing
            </template>
        </button>

        <vue-recaptcha v-if="recaptchaSiteKey" ref="recaptcha" :sitekey="recaptchaSiteKey" :loadRecaptchaScript="true"
                      @verify="recaptchaSuccess" size="invisible">
        </vue-recaptcha>

        <input :value="stripePaymentToken" name="stripeToken" type="hidden">
    </div>
</template>

<script>
import BaseStripeControl from '../base/base-stripe-control.vue'
import {mapGetters} from 'vuex'
import VueRecaptcha from 'vue-recaptcha'

export default {
        name: "checkout-payment-stripe",
        components: {
            BaseStripeControl, VueRecaptcha
        },
        mounted() {
            if (this.$refs.recaptcha) {
                this.$store.commit('checkout/recaptchaWidget', this.$refs.recaptcha);
            }
        },
        data() {
            return {
                stripePk: (ab.user.muser.hasStripe && !ab.user.muser.hasOldStripe) ? ab.user.muser.stripePk : null,
                cardholderName: null,
                addressLine1: null,
                addressLine2: null,
                country: null,
                city: null,
                constraints: {
                    cardholderName: {
                        presence: {
                            allowEmpty: false,
                            message: 'Please enter the Cardholder Name.'
                        }
                    },
                    city: {
                        presence: {
                            allowEmpty: false,
                            message: 'Please enter the billing address city.'
                        }
                    },
                    country: {
                        presence: {
                            allowEmpty: false,
                            message: 'Please enter the billing address country.'
                        }
                    },
                    addressLine1: {
                        presence: {
                            allowEmpty: false,
                            message: 'Please enter the billing address.'
                        }
                    },
                },
                validation: {
                    cardholderName: false,
                    cardNumber: false,
                    cardExpiry: false,
                    cardCvc: false,
                    country: false,
                    city: false,
                    addressLine1: false,
                    postZipCode: false,
                },
                postZipCode: null,
                recaptchaSiteKey: ab.recaptcha_site_key
            }
        },
        computed: {
            ...mapGetters('checkout', [
                'stripePaymentError',
                'stripePaymentToken',
                'stripeProcessing',
                'stripeRequestAddress',
                'stripeValidationTriggered',
                'containerSelector',
                'formFieldsIds',
                'firstInvalidFieldAfterSubmit',
            ]),
            countries() {
                return Object.entries(ab.countries).map((country) => ({
                    value: country[0],
                    text: country[1]
                }));
            },
        },
        watch: {
            cardholderName(value) {
                this.$store.commit('checkout/stripeCardholderName', value);
            },
            country(value) {
                this.$store.commit('checkout/stripeAddressCountry', value);
            },
            city(value) {
                this.$store.commit('checkout/stripeAddressCity', value);
            },
            addressLine1(value) {
                this.$store.commit('checkout/stripeAddressLine1', value);
            },
            addressLine2(value) {
                this.$store.commit('checkout/stripeAddressLine2', value);
            },
            postZipCode(value) {
                this.$store.commit('checkout/stripeAddressZip', value);
            },
            validation: {
                deep: true,
                handler(value) {
                    this.$store.commit('checkout/stripeValidation', value);
                }
            },
            stripePaymentError(value) {
                if (value) {
                    this.resetRecaptcha();
                }
            },
            firstInvalidFieldAfterSubmit(value) {
                if (value) {
                    this.resetRecaptcha();
                }
            }
        },
        methods: {
            tryPayment() {
                this.$store.dispatch('checkout/tryPayment');
            },
            onSelectCountryToggle(toggle) {
                this.$store.commit('checkout/continueShoppingButtonAnimation', !toggle);
            },
            recaptchaSuccess(response) {
                this.$store.commit('checkout/recaptchaResponse', response);
                this.tryPayment();
            },
            resetRecaptcha() {
                this.$refs.recaptcha.reset();
            }
        }
    }
</script>

<style lang="scss">
    .vc-checkout-payment-stripe {
        .grecaptcha-badge {
            position: absolute !important;
            bottom: -70px !important;
            right: calc(-186px - 4vw) !important;

            &:hover {
                right: -4vw !important;
            }

            @media screen and (max-width: 870px) {
                right: calc(-186px - 1.125rem) !important;

                &:hover {
                    right: -1.125rem !important;
                }
            }
        }
    }
</style>

<style lang="scss" scoped>
    .vc-checkout-payment-stripe {
        position: relative;
        margin-bottom: 50px;

        &__fields {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            grid-row-gap: 12px;
            grid-column-gap: 12px;
        }

        @media screen and(min-width: 1500px) {
            &__fields {
                grid-template-columns: repeat(2, minmax(270px, 1fr));
            }
        }

        &__submit {
            margin-top: 6px;
            width: 100%;
        }
    }
</style>